import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CatalogIcon } from '../types';
import { BUILT_IN_ICONS, getCatalog, saveCatalog } from '../data';

interface IconPickerModalProps {
  currentIcon: string;
  onSelect: (v: string) => void;
  onClose: () => void;
}

export function IconPickerModal({ currentIcon, onSelect, onClose }: IconPickerModalProps) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<CatalogIcon[]>(getCatalog);
  const [fileDataUrl, setFileDataUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [iconName, setIconName] = useState('');
  const [saveTocat, setSaveTocat] = useState(true);
  const [fileError, setFileError] = useState('');

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    setFileError('');
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/svg+xml', 'image/png'];
    if (!allowed.includes(file.type)) {
      setFileError(t('admin.builderInvalidFormat'));
      e.target.value = ''; return;
    }
    if (file.size > 200 * 1024) {
      setFileError(t('admin.builderFileTooLarge'));
      e.target.value = ''; return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      setFileDataUrl(ev.target?.result as string);
      setFileName(`${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
      if (!iconName) setIconName(file.name.replace(/\.[^.]+$/, ''));
    };
    reader.readAsDataURL(file);
  }

  function applyUpload() {
    if (!fileDataUrl) return;
    const name = iconName.trim() || t('admin.builderCustomIcon');
    if (saveTocat) {
      const updated = [...catalog, { id: `ic_${Date.now()}`, name, dataUrl: fileDataUrl }];
      saveCatalog(updated);
      setCatalog(updated);
    }
    onSelect(fileDataUrl);
    onClose();
  }

  function deleteCatalogIcon(id: string) {
    const updated = catalog.filter(c => c.id !== id);
    saveCatalog(updated);
    setCatalog(updated);
  }

  return (
    <div className="icp-overlay">
      <div className="icp-backdrop" onClick={onClose} />
      <div className="icp-modal">
        <div className="icp-modal-header">
          <h3><i className="fas fa-images" /> {t('admin.builderIconCatalogTitle')}</h3>
          <button className="icp-close-btn" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="icp-modal-body">

          {/* ── Iconos del sistema ── */}
          <div className="icp-section-label">{t('admin.builderSystemIcons')}</div>
          <div className="icp-grid">
            <div
              className={`icp-item${!currentIcon ? ' selected' : ''}`}
              onClick={() => { onSelect(''); onClose(); }}
            >
              <div className="icp-thumb icp-thumb-none"><i className="fas fa-ban" /></div>
              <span className="icp-item-name">{t('admin.builderNoIcon')}</span>
            </div>
            {BUILT_IN_ICONS.map(b => (
              <div
                key={b.value}
                className={`icp-item${currentIcon === b.value ? ' selected' : ''}`}
                onClick={() => { onSelect(b.value); onClose(); }}
              >
                <div className="icp-thumb"><img src={b.value} alt={b.label} /></div>
                <span className="icp-item-name">{b.label}</span>
              </div>
            ))}
          </div>

          {/* ── Mi catálogo ── */}
          <div className="icp-section-label">
            {t('admin.builderMyCatalog')} <span className="icp-count-badge">{catalog.length}</span>
          </div>
          {catalog.length > 0 ? (
            <div className="icp-grid">
              {catalog.map(c => (
                <div
                  key={c.id}
                  className={`icp-item${currentIcon === c.dataUrl ? ' selected' : ''}`}
                  onClick={() => { onSelect(c.dataUrl); onClose(); }}
                >
                  <div className="icp-thumb"><img src={c.dataUrl} alt={c.name} /></div>
                  <span className="icp-item-name">{c.name}</span>
                  <button
                    className="icp-del-btn"
                    onClick={e => { e.stopPropagation(); deleteCatalogIcon(c.id); }}
                    title={t('admin.builderDeleteFromCatalog')}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="icp-empty-catalog">
              <i className="fas fa-folder-open" /> {t('admin.builderCatalogEmpty')}
            </div>
          )}

          {/* ── Subir nuevo ícono ── */}
          <div className="icp-section-label">{t('admin.builderUploadIcon')}</div>
          <div className="icp-upload-zone">
            <div className="icp-upload-fields">
              <input
                type="text"
                className="form-input"
                placeholder={t('admin.builderIconNamePlaceholder')}
                value={iconName}
                onChange={e => setIconName(e.target.value)}
              />
              <label className="icp-file-label">
                <i className="fas fa-upload" /> {t('admin.builderSelectFile')}
                <input
                  type="file"
                  accept=".svg,.png,image/svg+xml,image/png"
                  style={{ display: 'none' }}
                  onChange={handleFile}
                />
              </label>
            </div>
            {fileError && <p className="icp-file-error">{fileError}</p>}
            {fileDataUrl && (
              <div className="icp-file-preview">
                <div className="icp-upload-preview">
                  <img src={fileDataUrl} alt="Preview" />
                  <span>{fileName}</span>
                </div>
              </div>
            )}
            <div className="icp-save-row">
              <label className="icp-catalog-toggle">
                <input
                  type="checkbox"
                  checked={saveTocat}
                  onChange={e => setSaveTocat(e.target.checked)}
                />
                <span><i className="fas fa-bookmark" /> {t('admin.builderSaveForReuse')}</span>
              </label>
              <button
                className="btn btn-primary btn-small"
                onClick={applyUpload}
                disabled={!fileDataUrl}
              >
                <i className="fas fa-check" /> {t('admin.builderUseIcon')}
              </button>
            </div>
            <small className="form-hint" style={{ marginTop: 8, display: 'block' }}>
              {t('admin.builderIconHelp')}
            </small>
          </div>

        </div>
      </div>
    </div>
  );
}
